import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Box, Typography } from '@mui/material';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import refresh from 'packages/novous_web/src/assets/icons/Reset.svg';
import arrowUp from 'packages/novous_web/src/assets/icons/ArrowBlack.svg';
import arrowDown from 'packages/novous_web/src/assets/icons/ArrowDownBlack.svg';
import PlusIcon from 'packages/novous_web/src/assets/icons/plusBlue.svg';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';

interface FilterDataModelProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subTitle?: string;
  handleFilterSave: (data: any) => void;
  description?: string;
  selectedUser?: any;
  filterData?: any;
}

interface GroupOption {
  code: string;
  label: string;
}

const FilterDeviceModal: React.FC<FilterDataModelProps> = ({
  open,
  onClose,
  title = 'Customise Table',
  subTitle = '',
  handleFilterSave,
  description = 'Choose which sections to display and arrange them the way you like.',
  selectedUser,
  filterData,
}) => {
  const { user } = useAuth();
  // Selections
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);

  // Expanded/collapsed states
  const [dataTableExpanded, setDataTableExpanded] = useState<boolean>(true);
  const [deviceExpanded, setDeviceExpanded] = useState<boolean>(true);
  const [vehicleExpanded, setVehicleExpanded] = useState<boolean>(true);

  const {
    data: vehiclesData,
    error: vehiclesError,
    loading: vehiclesLoading,
    fetchData: fetchvehiclesData,
  } = useAxios<any>(`${API_ENDPOINTS.dashboard.VEHICLE}`, 'POST');

  const {
    data: groupsData,
    error: groupsError,
    loading: groupsLoading,
    fetchData: fetchGroupsData,
  } = useAxios<{ data: any }>(
    `${API_ENDPOINTS.dashboard.GROUP}${user?.customer_id || selectedUser?.customer_id} `,
    'GET',
  );

  const {
    data: allDeviceData,
    error: allDeviceError,
    loading: allDeviceLoading,
    fetchData: fetchAllDeviceData,
  } = useAxios<any>(`${API_ENDPOINTS.directories.GET_ALL_DEVICES_FILTERED}`, 'POST');

  useEffect(() => {
    if (user?.customer_id || selectedUser?.customer_id) {
      fetchAllDeviceData({ fleetId: user?.customer_id || selectedUser?.customer_id });
      fetchGroupsData();
      fetchvehiclesData({ fleetId: user?.customer_id || selectedUser?.customer_id });
    }
  }, [user?.customer_id, selectedUser?.customer_id]);
  let groupOptions: GroupOption[] = [];

  if (groupsData && groupsData?.data) {
    groupOptions = groupsData?.data.map((item: string) => {
      return { code: item, label: item };
    });
  }

  // Memoize device options filtering for performance
  const deviceOptions: GroupOption[] = useMemo(() => {
    if (!allDeviceData) return [];

    let filteredDevices = allDeviceData.data || [];

    // Filter devices by selected group (if any)
    if (selectedGroup) {
      filteredDevices = filteredDevices.filter((item: any) => item.groupName === selectedGroup);
    }

    return filteredDevices.map((item: any) => {
      return {
        code: item.vehicleRegistrationNo,
        label: item.deviceId,
      };
    });
  }, [allDeviceData, selectedGroup]);

  // Memoize vehicle options filtering for performance
  const vehicleOptions: GroupOption[] = useMemo(() => {
    if (!vehiclesData) return [];

    let filteredVehicles = vehiclesData || [];

    // Filter vehicles by selected group (if any)
    if (selectedGroup) {
      filteredVehicles = filteredVehicles.filter((item: any) => item.groupName === selectedGroup);
    }

    return filteredVehicles.map((item: any) => {
      return {
        code: item.vehicleRegistrationNo,
        label: item.vehicleRegistrationNo,
      };
    });
  }, [vehiclesData, selectedGroup]);

  console.log(selectedGroup, 'selectedGroup');

  // Handlers
  const handleGroupChange = (newValue: string) => {
    setSelectedGroup(newValue);
    // Clear device and vehicle selections when group is selected
    setSelectedDevice(null);
    setSelectedVehicle(null);
  };

  const handleDeviceChange = (newValue: string) => {
    setSelectedDevice(newValue);
    // Disable vehicle selection when device is selected
    if (newValue) {
      setSelectedVehicle(null);
    }
  };

  const handleVehicleChange = (newValue: string) => {
    setSelectedVehicle(newValue);
    // Disable device selection when vehicle is selected
    if (newValue) {
      setSelectedDevice(null);
    }
  };

  const toggleDataTableExpanded = () => {
    setDataTableExpanded((prev) => !prev);
  };

  const toggleDeviceExpanded = () => {
    setDeviceExpanded((prev) => !prev);
  };

  const toggleVehicleExpanded = () => {
    setVehicleExpanded((prev) => !prev);
  };

  const handleReset = () => {
    setSelectedDevice(null);
    setSelectedGroup(null);
    setSelectedVehicle(null);
    handleFilterSave({
      group: null,
      device: null,
      vehicle: null,
    });
  };

  const handleSave = () => {
    // Perform save logic (send selected filters to parent or API)
    console.log('Filters Applied:', {
      group: selectedGroup,
      device: selectedDevice,
      vehicle: selectedVehicle,
    });
    handleFilterSave({
      group: selectedGroup,
      device: selectedDevice,
      vehicle: selectedVehicle,
    });
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="customise-table-title"
      aria-describedby="customise-table-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '85%', md: '880px' },
          maxWidth: '880px',
          height: { xs: '90vh', sm: '85vh', md: '720px' },
          maxHeight: '720px',
          bgcolor: '#FFFFFF',
          boxShadow: 24,
          padding: { xs: '16px', sm: '24px', md: '32px' },
          borderRadius: '16px',
          overflowY: 'auto',
          opacity: 1,
          border: '1px solid #F0EFF8',
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <div className="flex flex-col h-full justify-between">
          <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Inter Tight',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '24px',
                    lineHeight: '36px',
                    letterSpacing: '0%',
                    color: '#1B1B1B',
                  }}
                >
                  {title} {subTitle}
                </Typography>
                <CustomIconButton
                  icon={<img src={refresh} alt="reset" width={16} height={16} />}
                  text="Clear All"
                  background="transparent"
                  variant="outlined"
                  onClick={handleReset}
                />
              </div>

              {description && (
                <Typography
                  variant="button"
                  fontWeight={400}
                  sx={{
                    fontFamily: 'Inter Tight',
                    fontSize: '14px',
                    lineHeight: '21px',
                    color: '#808080',
                  }}
                >
                  {description}
                </Typography>
              )}
            </div>

            {/* Groups section */}
            <div className="flex flex-col gap-2">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={toggleDataTableExpanded}
              >
                <Typography variant="body1">Depots</Typography>

                <img
                  src={dataTableExpanded ? arrowUp : arrowDown}
                  width={24}
                  height={24}
                  alt={dataTableExpanded ? 'collapse' : 'expand'}
                />
              </div>

              {dataTableExpanded && (
                <div className="w-full max-w-[335px] relative">
                  <CustomDropDownInput
                    options={groupOptions.map(({ code, label }) => ({
                      value: code,
                      label,
                    }))}
                    value={selectedGroup || filterData?.group || ''}
                    onChange={handleGroupChange}
                    placeholder="Add depots"
                    isDisabled={!groupsData}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="absolute right-4 flex items-center gap-1 text-white font-bold text-sm"
                    style={{
                      fontFamily: 'Inter Tight',
                      fontSize: '16px',
                      fontWeight: 700,
                      lineHeight: '24px',
                      letterSpacing: '0%',
                      color: '#5547DD',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      pointerEvents: 'none',
                      top: '55%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    <img src={PlusIcon} alt="plus" width={16} height={16} /> Add
                  </button>
                </div>
              )}
            </div>

            {/* Device Type section */}
            <div className="flex flex-col gap-2">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={toggleDeviceExpanded}
              >
                <Typography variant="body1">Device</Typography>

                <img
                  src={deviceExpanded ? arrowUp : arrowDown}
                  width={24}
                  height={24}
                  alt={deviceExpanded ? 'collapse' : 'expand'}
                />
              </div>

              {deviceExpanded && (
                <div
                  className="w-full max-w-[335px] relative"
                  style={{
                    opacity: selectedVehicle ? 0.5 : 1,
                    pointerEvents: selectedVehicle ? 'none' : 'auto',
                  }}
                >
                  <CustomDropDownInput
                    options={deviceOptions.map(({ code, label }) => ({
                      value: code,
                      label,
                    }))}
                    value={selectedDevice || filterData?.device || ''}
                    onChange={handleDeviceChange}
                    placeholder="Select Device  "
                    isDisabled={Boolean(selectedVehicle) || !allDeviceData}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="absolute right-4 flex items-center gap-1 text-white font-bold text-sm"
                    style={{
                      fontFamily: 'Inter Tight',
                      fontSize: '16px',
                      fontWeight: 700,
                      lineHeight: '24px',
                      letterSpacing: '0%',
                      color: '#5547DD',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      pointerEvents: 'none',
                      top: '55%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    <img src={PlusIcon} alt="plus" width={16} height={16} /> Add
                  </button>
                </div>
              )}
            </div>

            {/* Vehicles section */}
            <div className="flex flex-col gap-2">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={toggleVehicleExpanded}
              >
                <Typography variant="body1">Vehicle</Typography>

                <img
                  src={vehicleExpanded ? arrowUp : arrowDown}
                  width={24}
                  height={24}
                  alt={vehicleExpanded ? 'collapse' : 'expand'}
                />
              </div>

              {vehicleExpanded && (
                <div
                  className="w-full max-w-[335px] relative"
                  style={{
                    opacity: selectedDevice ? 0.5 : 1,
                    pointerEvents: selectedDevice ? 'none' : 'auto',
                  }}
                >
                  <CustomDropDownInput
                    options={vehicleOptions.map(({ code, label }) => ({
                      value: code,
                      label,
                    }))}
                    value={selectedVehicle || filterData?.vehicle || ''}
                    onChange={handleVehicleChange}
                    placeholder="Select Vehicle  "
                    isDisabled={Boolean(selectedDevice) || !vehiclesData}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="absolute right-4 flex items-center gap-1 text-white font-bold text-sm"
                    style={{
                      fontFamily: 'Inter Tight',
                      fontSize: '16px',
                      fontWeight: 700,
                      lineHeight: '24px',
                      letterSpacing: '0%',
                      color: '#5547DD',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      pointerEvents: 'none',
                      top: '55%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    <img src={PlusIcon} alt="plus" width={16} height={16} /> Add
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 items-center justify-between">
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline
              variant="outlined"
              onClick={handleCancel}
            />
            <CustomIconButton text="Apply" variant="contained" onClick={handleSave} />
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default FilterDeviceModal;
